/**
 * Shared zod DTO schemas for API requests/responses.
 *
 * SCOPE: DTOs are added PER-STORY as their endpoints land (Stories 4–10 own
 * their route DTOs). Story 1 ships only what the skeleton/shared layer uses
 * now: the error envelope (see `errors.ts`), common id/pagination primitives,
 * and the `/healthz` liveness DTO. Do not add speculative DTOs for unbuilt
 * endpoints here.
 */
import { z } from 'zod';
// Re-exported below for consumers; ALSO imported locally because a re-export
// does not bind the name in this module and the home-section DTO uses both.
import { errorCodeSchema } from './errors.js';
export { errorEnvelopeSchema, errorCodeSchema } from './errors.js';
/** Every PK is a UUID (non-enumerable ids, PRD §8 OWASP / migration 0001). */
export const uuidSchema = z.uuid();
/** Hard page-size ceiling (dev plan: "pagination bounds (≤ 200/page, limit-clamp)"). */
export const PAGE_SIZE_MAX = 200;
/** Default page size when the client sends no `limit`. */
export const PAGE_SIZE_DEFAULT = 50;
/**
 * Common list-endpoint query: `limit` is CLAMPED to {@link PAGE_SIZE_MAX}
 * (over-asking is not an error — the plan's "limit-clamp" behaviour), while
 * non-positive / non-integer values are rejected as validation failures.
 */
export const paginationQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .transform((n) => Math.min(n, PAGE_SIZE_MAX))
        .default(PAGE_SIZE_DEFAULT),
    offset: z.coerce.number().int().min(0).default(0),
});
/** `/healthz` liveness response (TASK 1.5 route; liveness only, no auth). */
export const healthResponseSchema = z.strictObject({
    status: z.literal('ok'),
});
// ---------------------------------------------------------------------------
// Auth / session / admin (Story 2 — US-1 / US-2)
// ---------------------------------------------------------------------------
/** Access roles (mirror of the DB CHECK constraint). */
export const roleSchema = z.enum(['admin', 'member']);
/**
 * `GET /api/me` — the signed-in identity, role, and a fresh CSRF token the
 * client echoes on every state-changing request (double-submit). Returned only
 * to an authenticated, allowlisted session; unauthenticated callers get 401.
 */
export const meResponseSchema = z.strictObject({
    userId: uuidSchema,
    role: roleSchema,
    csrfToken: z.string().min(1),
});
/** One row of the admin allowlist list (AC 2.1). */
export const adminUserSchema = z.strictObject({
    id: uuidSchema,
    email: z.string().min(1),
    role: roleSchema,
    status: z.enum(['invited', 'active', 'removed']),
    invitedAt: z.string(),
    lastActiveAt: z.string().nullable(),
    /** True once a real account has linked (signed in at least once). */
    linked: z.boolean(),
    /**
     * The linked account id (`null` until first sign-in, and again after a
     * data purge). Used by the admin UI's self-row guard — the caller's own row
     * is the one whose `userId` equals the session's `userId` (AC 2.6/2.10) —
     * never for display.
     */
    userId: uuidSchema.nullable(),
});
export const adminUserListSchema = z.strictObject({
    items: z.array(adminUserSchema),
    total: z.number().int().min(0),
});
/** `POST /api/admin/users` — add by email (server re-validates the shape). */
export const addUserRequestSchema = z.strictObject({
    email: z.string().min(1),
});
/** `POST /api/admin/users/:id/role` — promote/demote. */
export const roleChangeRequestSchema = z.strictObject({
    role: roleSchema,
});
/** `POST /api/admin/users/:id/delete-data` — typed confirmation (email or DELETE). */
export const deleteDataRequestSchema = z.strictObject({
    confirm: z.string().min(1),
});
// ---------------------------------------------------------------------------
// Sync status + trigger (Story 3 — US-3, AC 3.5a)
// ---------------------------------------------------------------------------
/**
 * `GET /api/admin/sync` — the AC 3.5a minimal status readout. All six numeric/
 * flag readouts are read from the durable `service_counter` store (+ catalog
 * count), which is zero-seeded in migration 0001, so they are NEVER null even
 * before any producer runs. `lastRunStatus = 'never_run'` (with `lastRunAt =
 * null`) is the genuine first-run payload.
 */
export const syncStatusResponseSchema = z.strictObject({
    /** A run is in flight right now (advisory-lock probe). */
    running: z.boolean(),
    /** ISO timestamp of the most recent run, or null before any run. */
    lastRunAt: z.string().nullable(),
    lastRunStatus: z.enum(['running', 'succeeded', 'failed', 'never_run']),
    /** Available tracks currently in the catalog. */
    tracksIndexed: z.number().int().min(0),
    /** catalog_error_total / catalog_request_total (0 when no requests yet). */
    catalogErrorRate: z.number().min(0),
    /** stream_error_total / stream_request_total (0 when no streams yet). */
    streamingErrorRate: z.number().min(0),
    /** Durable telemetry-write-failure health counter. */
    telemetryWriteFailures: z.number().int().min(0),
});
/** `POST /api/admin/sync` — manual trigger result (already-running is not an error). */
export const syncTriggerResponseSchema = z.strictObject({
    status: z.enum(['enqueued', 'already_running']),
});
// ---------------------------------------------------------------------------
// Catalog browse — artists / albums / genres (Story 4 — TASK 4.1 / 4.3)
// ---------------------------------------------------------------------------
/**
 * Opaque KEYSET cursor (base64url of the sort-key tuple — see
 * apps/api/src/services/pagination.ts). Clients treat it as a blob: they echo
 * `nextCursor` back as `?cursor=`, never construct or interpret one. The bound
 * is a cheap DoS guard — a real cursor is well under 200 chars, so anything
 * larger is malformed by construction and is rejected before it reaches the
 * decoder.
 */
export const CURSOR_MAX_CHARS = 512;
export const cursorSchema = z.string().min(1).max(CURSOR_MAX_CHARS);
/**
 * Cursor-paginated list query. `limit` is CLAMPED to {@link PAGE_SIZE_MAX}
 * (over-asking is not an error, matching {@link paginationQuerySchema}); a
 * malformed/tampered `cursor` is a 422 VALIDATION, never a silent full scan.
 *
 * Keyset (not OFFSET) because the artist list is 2000+ rows on the bench
 * profile: OFFSET makes page N cost O(N * page) and shifts rows under a
 * concurrent sync write, which would duplicate/skip albums mid-scroll.
 */
export const cursorPageQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .transform((n) => Math.min(n, PAGE_SIZE_MAX))
        .default(PAGE_SIZE_DEFAULT),
    cursor: cursorSchema.optional(),
});
/** `nextCursor` is null on the LAST page — the client stops paging on null. */
const cursorPageFields = {
    nextCursor: z.string().nullable(),
};
/**
 * One row of `GET /api/artists` — name + how many albums hang off the artist.
 *
 * `imageRef` is the artist-image contract (migration 0009): the string
 * `'artists/<artistId>'` when a real image exists in the cover store, `null`
 * when the artist has no image yet. A client treats "has a photo" as
 * `imageRef?.startsWith('artists/')` and loads the image from
 * `/api/artist-image/<artistId>` — the ref itself is never a URL.
 */
export const artistListItemSchema = z.strictObject({
    id: uuidSchema,
    name: z.string().min(1),
    albumCount: z.number().int().min(0),
    imageRef: z.string().nullable(),
});
export const artistListResponseSchema = z.strictObject({
    items: z.array(artistListItemSchema),
    ...cursorPageFields,
});
/**
 * An album tile in ANY grid (artist page, genre page; reused by search/home).
 *
 * `year` is OPTIONAL, never nullable: an album with no year OMITS the key
 * entirely so a client can never render the string "null" (AC 4.2). This is
 * `.optional()` on purpose — switching it to `.nullable()` would reintroduce
 * exactly the bug the AC forbids.
 *
 * `available: false` marks an album whose Drive folder disappeared. Such albums
 * STAY in browse grids carrying this flag (AC 3.9) — the client renders a "no
 * longer available" marker rather than hiding the row.
 */
export const albumCardSchema = z.strictObject({
    id: uuidSchema,
    title: z.string().min(1),
    year: z.number().int().optional(),
    coverRef: z.string().nullable(),
    available: z.boolean(),
    artistId: uuidSchema,
    artistName: z.string().min(1),
});
/** Minimal artist/genre header echoed alongside a paginated album grid. */
export const catalogRefSchema = z.strictObject({
    id: uuidSchema,
    name: z.string().min(1),
});
/**
 * Artist header ref — {@link catalogRefSchema} plus the artist-image pointer.
 * A SEPARATE schema (rather than adding `imageRef` to `catalogRefSchema`)
 * because the plain ref is shared by genres, playlists and search groups,
 * none of which carry an image; only artist objects do (see
 * {@link artistListItemSchema} for the `imageRef` semantics).
 */
export const artistRefSchema = z.strictObject({
    ...catalogRefSchema.shape,
    imageRef: z.string().nullable(),
});
/** `GET /api/artists/:id` — the artist's albums, year ascending, no-year LAST. */
export const artistAlbumsResponseSchema = z.strictObject({
    artist: artistRefSchema,
    items: z.array(albumCardSchema),
    ...cursorPageFields,
});
/**
 * `GET /api/albums` — the WHOLE catalog as album tiles, for the browse
 * ALBUMS tab. No header object: unlike the artist and genre album pages this
 * page is not scoped to a parent, so `items` + the cursor is the entire body.
 *
 * Ordered artist-then-year like the genre page (`getGenreAlbums`), which is the
 * order that makes a 10k-album list navigable — an album is found by the artist
 * you are looking for, and a pure title sort would interleave every artist.
 */
export const albumListResponseSchema = z.strictObject({
    items: z.array(albumCardSchema),
    ...cursorPageFields,
});
/**
 * One track row on album-detail.
 *
 * `available: false` tracks ARE returned here (album-detail is the documented
 * exception to the AC 3.9 browse exclusion) so the client can render the "no
 * longer available" marker in place instead of silently renumbering the album.
 *
 * `formatSupported` is the SERVER-side verified-matrix baseline (supported on
 * both target engines without a version gate); per AC 5.7 the client's runtime
 * `canPlayType` probe is authoritative and may override it either way.
 *
 * `playCount` is the CALLER'S all-time QUALIFYING play count (AC 12.1) for
 * this track — user-scoped, which is what made `GET /api/albums/:id` a
 * per-user response (see {@link albumDetailResponseSchema}). `min(0)`, NOT
 * `min(1)` like the stats entries: a stats row exists only BECAUSE the track
 * was played, whereas every album track appears here and `0` is the genuine
 * "never played" state, not a missing key.
 */
export const albumTrackSchema = z.strictObject({
    id: uuidSchema,
    title: z.string().min(1),
    discNo: z.number().int().nullable(),
    trackNo: z.number().int().nullable(),
    durationS: z.number().int().min(0),
    format: z.string().min(1),
    formatSupported: z.boolean(),
    available: z.boolean(),
    playCount: z.number().int().min(0),
});
/**
 * `GET /api/albums/:id` — header + tracks ordered disc then track (AC 5.1).
 *
 * `trackCount` / `totalDurationS` cover EVERY track of the album, available or
 * not, because album-detail displays them all. When `tracksTruncated` is true
 * the `tracks` array was capped (pathological album) and `trackCount` remains
 * the authoritative total.
 *
 * `playCount` (header) is the sum of ALL the album's tracks' user-scoped
 * all-time qualifying play counts — computed over the whole album in SQL, so
 * like `trackCount` it stays authoritative even when `tracksTruncated` capped
 * the array. Adding it made this response PER-USER: the route serves it
 * `private, no-store` (the home/stats cache rule), no longer the shared
 * catalog `private, max-age=60`.
 */
export const albumDetailResponseSchema = z.strictObject({
    id: uuidSchema,
    title: z.string().min(1),
    year: z.number().int().optional(),
    coverRef: z.string().nullable(),
    available: z.boolean(),
    artist: catalogRefSchema,
    trackCount: z.number().int().min(0),
    totalDurationS: z.number().int().min(0),
    playCount: z.number().int().min(0),
    tracks: z.array(albumTrackSchema),
    tracksTruncated: z.boolean(),
});
/**
 * One row of `GET /api/genres`. `albumCount` is the album-genre UNION computed
 * at query time: an album counts once if ANY of its tracks carries the genre.
 */
export const genreListItemSchema = z.strictObject({
    id: uuidSchema,
    name: z.string().min(1),
    albumCount: z.number().int().min(0),
});
export const genreListResponseSchema = z.strictObject({
    items: z.array(genreListItemSchema),
    ...cursorPageFields,
});
/** `GET /api/genres/:id` — the genre's albums, artist then year ascending. */
export const genreAlbumsResponseSchema = z.strictObject({
    genre: catalogRefSchema,
    items: z.array(albumCardSchema),
    ...cursorPageFields,
});
// ---------------------------------------------------------------------------
// Lyrics — synced/plain lyrics per track (LRCLIB enrichment, migration 0010)
// ---------------------------------------------------------------------------
/**
 * One synced-lyrics cue. `time` is SECONDS (float, e.g. 7.49) and non-negative
 * by contract — the shared parser (`parseLrc`, packages/shared/src/lyrics.ts)
 * clamps any offset-shifted negative to 0, so a value this schema accepts can
 * never come back from the parser as a rejection. `text` may be '' — a blank
 * timed cue is a deliberate gap, not an error.
 */
export const lyricsLineSchema = z.strictObject({
    time: z.number().nonnegative(),
    text: z.string(),
});
/**
 * `GET /api/tracks/:id/lyrics` — the lyrics resolution for ONE track.
 *
 * FOUR mutually-exclusive states in `status`, and the two nullable payload
 * fields are populated EXACTLY per state (a strict contract the frontend can
 * switch on without guessing):
 *
 *   'synced'       synced LRC available → `lines` is the parsed cue array
 *                  (non-null, possibly empty), `plain` is null.
 *   'plain'        only unsynced text   → `plain` is that text, `lines` null.
 *   'instrumental' the track is instrumental (no lyrics) → both null.
 *   'none'         no lyrics row, or a row checked-but-empty (LRCLIB found
 *                  nothing) → both null. NOT a 404: the TRACK exists, it just
 *                  has no lyrics.
 *
 * `lines` is present (non-null) IFF `status === 'synced'`; `plain` is present
 * (non-null) IFF `status === 'plain'`. Both are always PRESENT keys (nullable,
 * not optional) so a client reads one shape regardless of state. The 'synced'
 * state does NOT also carry `plain` (kept null) — the deterministic choice, so
 * the payload for a given status is exactly one non-null field or none.
 */
export const lyricsStatusSchema = z.enum(['synced', 'plain', 'instrumental', 'none']);
export const lyricsResponseSchema = z.strictObject({
    trackId: z.string(),
    status: lyricsStatusSchema,
    /** Present (non-null) iff `status === 'synced'`; may be an empty array. */
    lines: z.array(lyricsLineSchema).nullable(),
    /** Present (non-null) iff `status === 'plain'`. */
    plain: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Search — grouped, ranked, independently paged (Story 4 — TASK 4.2, AC 11.x)
// ---------------------------------------------------------------------------
/**
 * BOUNDS ON THE SEARCH TERM. `q` is the most attacker-reachable input in the
 * catalog surface: it flows into three GIN trigram predicates at once.
 *
 *   MIN 2 — shorter terms are REJECTED (422), not silently emptied. A 1-char
 *     term matches a double-digit percentage of a 100k-track catalog, so it is
 *     both useless to the user and the cheapest way to make the server sort the
 *     whole catalog. Rejecting rather than returning `[]` is also the honest
 *     contract: an empty group means "nothing matched", and the client's
 *     "No results for X" copy would otherwise lie about a term never run.
 *
 *   MAX 100 — longer terms are REJECTED, not clamped. Truncating to 100 would
 *     silently search for something the user did not type and report the
 *     results as if it had; no real artist/album/track title approaches this
 *     length, so anything longer is malformed by construction.
 *
 * Both bounds are measured AFTER trimming, so whitespace padding cannot be used
 * to smuggle a 1-char term past the minimum.
 */
export const SEARCH_QUERY_MIN_CHARS = 2;
export const SEARCH_QUERY_MAX_CHARS = 100;
/** The four result groups. `type` narrows a request to exactly one of them. */
export const searchGroupNameSchema = z.enum(['artists', 'albums', 'songs', 'genres']);
/**
 * `GET /api/search` query.
 *
 * With no `type` the response carries the FIRST page of every group (the
 * grouped results panel). With `type` it carries that one group only, which is
 * what makes pagination INDEPENDENT PER GROUP: a `cursor` addresses a position
 * inside a single group's ranking, so it is meaningless — and rejected — without
 * the `type` that says which group it belongs to.
 *
 * STRICT, like {@link searchResponseSchema} on the way out. It was `z.object`,
 * so `?typ=songs` was silently DROPPED and the caller got all four groups back
 * while believing they had narrowed to one — the client then renders the wrong
 * panel and nothing anywhere reports an error. Every parameter this endpoint
 * accepts is enumerated here, so an unknown one is a typo by definition and
 * 422 is the answer that lets the caller find it.
 *
 * =========================================================================
 * THE `?cursor=` CLIENT CONTRACT: 422 MEANS "RESTART THIS GROUP"
 * =========================================================================
 * A `422 VALIDATION` carrying `fieldErrors.cursor` on a `?cursor=` request is
 * NOT a bug and NOT a reason to retry the same cursor. It means the listing
 * under the cursor changed in a way that makes the position meaningless, and
 * the server refuses to guess. It is reachable in ordinary operation:
 *
 *   * the anchor row was DELETED (the sync job hard-deletes orphan genres on
 *     every run) or was RETITLED, so it moved in the ordering;
 *   * for search specifically, the SEARCH TERM or the GENRE CHIP changed
 *     between minting the cursor and sending it — which a debounced search box
 *     racing an in-flight "load more" produces without any user error;
 *   * a concurrent sync deleted exactly the remainder of the listing.
 *
 * The required client behaviour is the same in every case: DISCARD the cursor
 * and re-request that group from page 1 (same `q`, same `type`, same
 * `genreId`, no `cursor`), replacing the accumulated list rather than appending
 * to it. Retrying the cursor will 422 forever; treating it as a fatal error
 * strands the user mid-scroll. The alternative the server rejects — quietly
 * returning "some page" — is what would actually corrupt the list, by skipping
 * or repeating an arbitrary interval with a 200.
 */
export const searchQuerySchema = z
    .strictObject({
    q: z.string().trim().min(SEARCH_QUERY_MIN_CHARS).max(SEARCH_QUERY_MAX_CHARS),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .transform((n) => Math.min(n, PAGE_SIZE_MAX))
        .default(PAGE_SIZE_DEFAULT),
    cursor: cursorSchema.optional(),
    type: searchGroupNameSchema.optional(),
    /** Genre filter chip (AC 11.9): narrows artists/albums/songs to one genre. */
    genreId: uuidSchema.optional(),
})
    .refine((v) => v.cursor === undefined || v.type !== undefined, {
    path: ['cursor'],
    message: 'A cursor is per-group; send `type` to say which group it pages.',
})
    /**
     * `type=genres` and `genreId` are MUTUALLY EXCLUSIVE (UI contract §10.6).
     *
     * The response contract already says the `genres` group is ABSENT while a
     * chip is active, so this pair asks for exactly the one group the server is
     * required not to send. It used to answer `200 {query, genreId}` — a body
     * with no group in it at all, which validates only because every group is
     * optional. Adding `?cursor=` made it worse: the pair passed the refine above
     * (a `type` IS present), and the cursor was then silently discarded along
     * with the group it was paging.
     *
     * Rejected at the boundary rather than tolerated, for the same reason this
     * object is `strictObject`: having just made a MISSPELLED parameter a 422, it
     * would be incoherent to keep silently swallowing a correctly-spelled but
     * self-contradictory one. A client that wants genres must drop the chip.
     */
    .refine((v) => v.type !== 'genres' || v.genreId === undefined, {
    path: ['type'],
    message: 'The genres group is not available while a genre filter is applied.',
});
/**
 * A standalone track row carrying enough album/artist context to render and
 * play it without a second request. The track-shaped counterpart to
 * {@link albumCardSchema}: reused by search's songs group and by the stats
 * Top-Songs list, so the two cannot drift.
 *
 * `available` is a FLAG, not a filter — what it means depends on the surface,
 * and each consumer documents its own rule (see {@link searchTrackSchema}).
 */
export const trackCardSchema = z.strictObject({
    id: uuidSchema,
    title: z.string().min(1),
    durationS: z.number().int().min(0),
    format: z.string().min(1),
    formatSupported: z.boolean(),
    available: z.boolean(),
    albumId: uuidSchema,
    albumTitle: z.string().min(1),
    coverRef: z.string().nullable(),
    artistId: uuidSchema,
    artistName: z.string().min(1),
});
/**
 * One track row in the search songs group — {@link trackCardSchema} exactly.
 *
 * `available` is present and always `true` HERE BY CONSTRUCTION: search is a
 * browse surface, so it excludes `available=false` tracks (AC 3.9 — a track
 * removed from Drive leaves search). The field is kept rather than dropped so
 * the exclusion is observable in the contract instead of being an invisible
 * WHERE clause. Stats reuses the same shape but does NOT apply that exclusion,
 * because a user's own listening history is not a browse surface.
 */
export const searchTrackSchema = trackCardSchema;
/**
 * A group of ranked results.
 *
 * `count` is the TOTAL number of matches across ALL pages, not the size of
 * `items` (AC 11.1 count semantics) — it is what the group header renders
 * ("Songs 214") and what tells the client a "see all" affordance is worth
 * showing. `items.length <= limit <= 200`; `nextCursor` is null on the last
 * page of THIS group and says nothing about any other group.
 */
function searchGroupSchema(item) {
    return z.strictObject({
        items: z.array(item),
        count: z.number().int().min(0),
        ...cursorPageFields,
    });
}
/**
 * `GET /api/search` — results grouped by type.
 *
 * Every group is OPTIONAL, and absence is meaningful in exactly two ways:
 *   * a `type`-narrowed request returns only the requested group;
 *   * `genres` is OMITTED whenever a genre filter is active (UI contract
 *     §10.6): the chip has already committed the user to one genre, so
 *     offering genres to pick from would be a dead end. This is deliberately
 *     an ABSENT key rather than an empty group, so the client renders no
 *     genre section at all rather than an empty one.
 */
export const searchResponseSchema = z.strictObject({
    /** The trimmed term actually searched — echoed so the client can label results. */
    query: z.string().min(1),
    /** Echoes the active genre-filter chip, absent when no filter is applied. */
    genreId: uuidSchema.optional(),
    artists: searchGroupSchema(artistRefSchema).optional(),
    albums: searchGroupSchema(albumCardSchema).optional(),
    songs: searchGroupSchema(searchTrackSchema).optional(),
    genres: searchGroupSchema(catalogRefSchema).optional(),
});
// ---------------------------------------------------------------------------
// Home & Stats (Story 4 — TASK 4.4, AC 12.2 / 12.5 / 14.1 / 14.2 / 14.3)
// ---------------------------------------------------------------------------
/**
 * Fixed section sizes, normative in the ACs rather than client-tunable — which
 * is why neither endpoint accepts a `limit` at all. Home is a landing surface
 * with two fixed-size rails, not a paginated listing; making the sizes
 * negotiable would let a client ask for 200 recently-played contexts and turn a
 * bounded scan into an unbounded one.
 */
export const RECENTLY_ADDED_LIMIT = 50; // AC 14.1
export const RECENTLY_PLAYED_LIMIT = 20; // AC 14.2
export const STATS_TOP_LIMIT = 25; // AC 12.2
/**
 * Stats period. `last_30_days` is a ROLLING 30x24 h window computed in UTC
 * (AC 12.2 / PRD §10) — deliberately not "the last 30 calendar days", which
 * would include a partial 31st day's worth of plays.
 */
export const statsPeriodSchema = z.enum(['all_time', 'last_30_days']);
/**
 * `GET /api/stats` query.
 *
 * NOTE WHAT IS ABSENT: there is no user id, in any form. Stats are resolved
 * from the authenticated session ONLY (AC 12.5), so "read another user's stats"
 * is not a request this API can express. A plain `z.object` (not `strictObject`)
 * is used on purpose: unknown query keys are STRIPPED rather than rejected, so a
 * client that appends `?userId=<someone-else>` gets its own stats back and the
 * parameter provably does nothing. The security integration test asserts exactly
 * that, rather than merely asserting the key is absent from the type.
 */
export const statsQuerySchema = z.object({
    period: statsPeriodSchema.default('all_time'),
});
/** One Top-Songs row. `rank` is 1-based and echoes the array position. */
export const statsSongEntrySchema = z.strictObject({
    rank: z.number().int().min(1),
    /** Qualifying plays (AC 12.1) in the selected period. Always >= 1. */
    playCount: z.number().int().min(1),
    /** Most recent QUALIFYING play — the AC 12.2 tie-break value, exposed so the
     *  ordering is auditable by clients and tests rather than opaque. */
    lastPlayedAt: z.string(),
    track: trackCardSchema,
});
/** One Top-Albums row; the album is the same card every grid renders. */
export const statsAlbumEntrySchema = z.strictObject({
    rank: z.number().int().min(1),
    playCount: z.number().int().min(1),
    lastPlayedAt: z.string(),
    album: albumCardSchema,
});
/**
 * `GET /api/stats` — Top 25 songs + Top 25 albums for one period (AC 12.2).
 *
 * Empty arrays are the genuine "no qualifying plays yet" state (AC 12.5 empty
 * state), never an error.
 */
export const statsResponseSchema = z.strictObject({
    period: statsPeriodSchema,
    /**
     * Inclusive lower bound of the rolling window as an ISO instant, or null for
     * all-time. Echoed so a client can label the period honestly ("since <date>")
     * and so tests can assert the window is an INSTANT 30x24 h back, not a
     * midnight-truncated calendar boundary.
     */
    windowStart: z.string().nullable(),
    topSongs: z.array(statsSongEntrySchema).max(STATS_TOP_LIMIT),
    topAlbums: z.array(statsAlbumEntrySchema).max(STATS_TOP_LIMIT),
});
/**
 * One Recently-Played rail entry (AC 14.2): the playback CONTEXT a play started
 * from, not the track. Discriminated on `contextType` so a client renders an
 * album tile or a playlist tile without guessing which optional field is set.
 *
 * Only `album` and `playlist` contexts appear. `favorites` / `queue` / `search`
 * contexts — and null-context standalone plays — are excluded by the AC, which
 * scopes this rail to "the user's last 20 distinct albums/playlists".
 *
 * An album entry may carry `available: false`: a removed album KEEPS its
 * position and renders with the AC 3.9 "no longer available" treatment. It is
 * emphatically not filtered out, and it must not be tappable into a 404.
 */
export const recentlyPlayedEntrySchema = z.discriminatedUnion('contextType', [
    z.strictObject({
        contextType: z.literal('album'),
        /** Most recent QUALIFYING play in this context — sets the rail position. */
        lastPlayedAt: z.string(),
        album: albumCardSchema,
    }),
    z.strictObject({
        contextType: z.literal('playlist'),
        lastPlayedAt: z.string(),
        playlist: catalogRefSchema,
    }),
]);
/**
 * `GET /api/recently-played-tracks` section size. Fixed like its siblings
 * above — the endpoint accepts no `limit` — and deliberately a SEPARATE
 * constant from {@link RECENTLY_PLAYED_LIMIT} even though both are 20 today:
 * the contexts rail and the tracks list are different surfaces, and resizing
 * one must never silently resize the other.
 */
export const RECENTLY_PLAYED_TRACKS_LIMIT = 20;
/**
 * One recently-played TRACK entry — the track-level counterpart of
 * {@link recentlyPlayedEntrySchema}, which deals in playback CONTEXTS
 * (albums/playlists). This list answers a different question: "which songs
 * did I actually listen to lately?", so it INCLUDES tracks whose plays were
 * standalone or started from favorites/queue/search — the plays the contexts
 * rail excludes.
 *
 * `playCount` is the user's ALL-TIME qualifying count for the track, `min(1)`
 * like the stats entries: a track appears here only BECAUSE it has at least
 * one qualifying play (AC 12.1). `lastPlayedAt` is the most recent QUALIFYING
 * play as an ISO instant (same format as the stats entries' `lastPlayedAt`)
 * and is the sort key, exposed so the ordering is auditable.
 *
 * The track's `available` flag is a FLAG, not a filter — history keeps a
 * removed track in place, flagged, exactly like the sibling surfaces (AC 3.9).
 */
export const recentlyPlayedTrackEntrySchema = z.strictObject({
    track: trackCardSchema,
    playCount: z.number().int().min(1),
    lastPlayedAt: z.string(),
});
/**
 * `GET /api/recently-played-tracks` — the caller's last
 * {@link RECENTLY_PLAYED_TRACKS_LIMIT} distinct tracks with a qualifying play,
 * most recent first. Like home/stats it accepts NO user id in any form
 * (AC 12.5 isolation pattern) and no query parameters at all; an empty `items`
 * is the genuine "no qualifying plays yet" state, never an error.
 */
export const recentlyPlayedTracksResponseSchema = z.strictObject({
    items: z.array(recentlyPlayedTrackEntrySchema).max(RECENTLY_PLAYED_TRACKS_LIMIT),
});
/**
 * PER-SECTION RESULT (AC 14.3) — the shape that makes home partially failable.
 *
 * Home renders two independent rails fed by two unrelated queries: one reads the
 * global catalog, the other reads the caller's play history. A single
 * `{ recentlyAdded: [], recentlyPlayed: [] }` body would force an all-or-nothing
 * response, so a transient failure in either query would blank the whole page.
 * Instead each section carries its OWN status and the client renders a per-rail
 * error + Retry (the AC 4.5 pattern) beside a rail that served normally.
 *
 * Three states, discriminated on `status`:
 *   ok     — at least one item; `items` is non-empty BY SCHEMA, so "ok with
 *            nothing in it" is unrepresentable and can never be mistaken for
 *            an empty state.
 *   empty  — the query succeeded and there is genuinely nothing (new library /
 *            no plays yet). Explicit rather than implied by a zero-length array,
 *            so the client picks the empty-state copy without a length check.
 *   error  — this section failed. `code` is a stable shared error code; no
 *            internal detail crosses the boundary (it goes to the logs, keyed
 *            by request id).
 */
function homeSectionSchema(item) {
    return z.discriminatedUnion('status', [
        z.strictObject({ status: z.literal('ok'), items: z.array(item).min(1) }),
        z.strictObject({ status: z.literal('empty') }),
        z.strictObject({ status: z.literal('error'), code: errorCodeSchema }),
    ]);
}
/**
 * `GET /api/home` — the two landing rails, independently resolved.
 *
 * Like {@link statsQuerySchema} this endpoint accepts NO user id (AC 12.5 / the
 * AC 7.7 / 9.5 / 10.4 isolation pattern): recently-played is scoped to the
 * authenticated session and nothing else. It takes no query parameters at all.
 */
export const homeResponseSchema = z.strictObject({
    /** AC 14.1: 50 newest albums by `first_indexed_at` DESC, tie-break `id` DESC. */
    recentlyAdded: homeSectionSchema(albumCardSchema),
    /** AC 14.2: last 20 distinct album/playlist contexts, most recent first. */
    recentlyPlayed: homeSectionSchema(recentlyPlayedEntrySchema),
});
// ---------------------------------------------------------------------------
// Per-user state — favorites, playlists, queue, history, play events
// (Story 6 — US-7 / US-8 / US-9 / US-10 / US-12)
// ---------------------------------------------------------------------------
/**
 * =========================================================================
 * WHAT IS ABSENT FROM EVERY SCHEMA BELOW, AND WHY
 * =========================================================================
 *
 * 1. NO USER ID, in any form — not a path segment, not a query key, not a body
 *    field. Every per-user endpoint resolves its subject from the signed
 *    session cookie via `authenticatedUserId` and nothing else, so "read or
 *    mutate another user's state" is not a request this API can EXPRESS
 *    (AC 7.7 / 9.5 / 10.4). Cross-user ids that reach a handler as a RESOURCE
 *    id (a playlist id, a queue-history id) answer 404 — existence
 *    non-disclosure, never 403.
 *
 * 2. NO CLIENT TIMESTAMP. Conflict resolution is Last-Write-Wins on the
 *    SERVER-assigned `updated_at` maintained by the `set_updated_at` triggers
 *    (AC 7.5 / 9.7 / 10.6). A client-supplied "modifiedAt" would let a device
 *    with a skewed clock win every race, so no such field exists. The one
 *    client-supplied instant in this whole block is
 *    {@link playEventInputSchema}'s `playedAt`, and that is DOMAIN DATA (when
 *    the music actually played, which only the client can know — offline
 *    batches merge days later) rather than a conflict-resolution token. It is
 *    never compared against anything to decide a winner.
 *
 * 3. NO UNBOUNDED LIST. Every read below is cursor-paginated at
 *    <= {@link PAGE_SIZE_MAX}, including the queue reconciliation payload,
 *    which is chunked separately at {@link QUEUE_RECONCILE_CHUNK_MAX}.
 */
/**
 * A client-generated idempotency key on an ADD-type mutation. Bounded because
 * it is stored verbatim in a `text` column carrying a unique index; 128 chars
 * comfortably fits a UUID or a ULID and anything longer is malformed by
 * construction rather than useful.
 *
 * SEMANTICS (the same on favorites, playlist adds, queue adds and queue-history
 * records — every add-type mutation in the API accepts one):
 *   * SAME key, SAME request -> a RETRY. Nothing is inserted a second time and
 *     the response is BYTE-IDENTICAL to the first call's.
 *   * SAME key, DIFFERENT request -> 422 with `fieldErrors.idempotencyKey`.
 *     This is the one case with no obviously right answer, so it is spelled
 *     out: the alternative (return the first request's result) tells the caller
 *     "done" about something that was never applied — e.g. "favorite track B"
 *     answered with track A's favorite, which the client then renders as if B
 *     were favorited. A key identifies ONE request, not a slot to reuse, and
 *     reusing one is a client bug worth surfacing rather than absorbing.
 *     Sameness is compared on what the mutation would DO: the target for a
 *     favorite, the (track, reason) for a history record, and a digest of the
 *     op kind + track ids for the many-row playlist/queue adds.
 *   * NEW key, same target -> a deliberate "add anyway" (AC 9.2's duplicate
 *     confirmation), which does insert again where the surface allows
 *     duplicates (playlists, queue) and is a no-op where it does not
 *     (favorites, whose natural key is unique per user).
 *   * NO key -> best-effort; the natural key still prevents a duplicate
 *     favorite, but a repeated playlist/queue add appends again.
 *
 * HOW LONG A KEY IS REMEMBERED. For the single-row mutations (favorite,
 * history record) the key lives on the row it created and dies with it — the
 * row IS the answer a replay returns. For the many-row adds (a whole album into
 * a playlist, a batch into the queue) the proof is a separate
 * `idempotency_receipt` row (migration 0006) that is NOT part of the result, so
 * deleting one added track can no longer make a replay add the album again —
 * which is what this PWA's offline outbox, replaying mutations days later,
 * depends on.
 */
export const IDEMPOTENCY_KEY_MAX_CHARS = 128;
export const idempotencyKeySchema = z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX_CHARS);
/**
 * A reorder instruction, shared verbatim by playlists (AC 9.3) and the queue
 * (AC 7.1). `to` is the ACCESSIBLE, non-gesture path the AC requires: the
 * client names a destination index and the SERVER recomputes contiguous
 * positions, so a keyboard/screen-reader user never has to express a drag.
 *
 * A destination beyond either end is CLAMPED rather than rejected — "move to
 * position 999" in a 12-track playlist unambiguously means "move it to the
 * end", and erroring would be a worse answer than the obvious one. The clamp
 * is a pure function (`moveTargetIndex` in apps/api/src/services/user-state.ts)
 * so it is unit-testable without a database.
 */
export const moveDirectiveSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('up') }),
    z.strictObject({ kind: z.literal('down') }),
    z.strictObject({ kind: z.literal('top') }),
    z.strictObject({ kind: z.literal('bottom') }),
    z.strictObject({ kind: z.literal('to'), position: z.number().int().min(0) }),
]);
// ---------------------------------------------------------------------------
// Favorites (TASK 6.1 — US-10)
// ---------------------------------------------------------------------------
/** Favorites are polymorphic over tracks and albums (migration 0001 CHECK). */
export const favoriteTargetTypeSchema = z.enum(['track', 'album']);
/**
 * `GET /api/favorites` query.
 *
 * PAGINATION SHAPE — why two sections rather than one merged list. The screen
 * renders "Songs" and "Albums" as SEPARATE sections (AC 10.4), and the two are
 * different row types drawn from different joins. A single interleaved cursor
 * would have to encode which section a position belongs to and would make the
 * two sections' scroll positions inseparable. So:
 *
 *   no `type`  -> the FIRST page of BOTH sections, each independently bounded
 *                 and each carrying its own `nextCursor`;
 *   `type=...` -> that one section only, and `cursor` addresses a position
 *                 inside it.
 *
 * A `cursor` without a `type` is therefore meaningless and rejected — exactly
 * the rule {@link searchQuerySchema} applies to its per-group cursors. `limit`
 * applies PER SECTION, so the no-`type` response is bounded at 2 x limit rows,
 * never more.
 */
export const favoritesQuerySchema = z
    .object({
    type: favoriteTargetTypeSchema.optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .transform((n) => Math.min(n, PAGE_SIZE_MAX))
        .default(PAGE_SIZE_DEFAULT),
    cursor: cursorSchema.optional(),
})
    .refine((v) => v.cursor === undefined || v.type !== undefined, {
    path: ['cursor'],
    message: 'A cursor is per-section; send `type` to say which section it pages.',
});
/**
 * One favorited song. `favoritedAt` is the SERVER-assigned `created_at`, which
 * is also the sort key (newest first), so the ordering a client renders is the
 * ordering it can verify.
 *
 * The track is the same {@link trackCardSchema} every other surface renders,
 * and its `available` flag is a FLAG here, not a filter: a favorited track
 * whose Drive file vanished stays in the list carrying `available: false`
 * (AC 3.9 — favorites are explicitly NOT a browse surface). Only ADDING a new
 * favorite requires availability.
 */
export const favoriteTrackEntrySchema = z.strictObject({
    favoriteId: uuidSchema,
    favoritedAt: z.string(),
    track: trackCardSchema,
});
export const favoriteAlbumEntrySchema = z.strictObject({
    favoriteId: uuidSchema,
    favoritedAt: z.string(),
    album: albumCardSchema,
});
function favoritesSectionSchema(item) {
    return z.strictObject({ items: z.array(item), ...cursorPageFields });
}
/**
 * `GET /api/favorites` — Songs and/or Albums, each independently paged.
 *
 * A section is ABSENT (not empty) when a `type`-narrowed request excluded it,
 * so "no albums section" and "no favorited albums" are distinguishable.
 */
export const favoritesResponseSchema = z.strictObject({
    tracks: favoritesSectionSchema(favoriteTrackEntrySchema).optional(),
    albums: favoritesSectionSchema(favoriteAlbumEntrySchema).optional(),
});
/** `POST /api/favorites` — idempotent "ensure favorited" (see the response DTO). */
export const addFavoriteRequestSchema = z.strictObject({
    targetType: favoriteTargetTypeSchema,
    targetId: uuidSchema,
    idempotencyKey: idempotencyKeySchema.optional(),
});
/**
 * The favorite RESOURCE — what `POST /api/favorites` returns, always with 200.
 *
 * IT DELIBERATELY REPORTS STATE, NOT WHAT THE CALL DID. There is no `created`
 * flag, and that omission is the whole idempotency contract: a retried add
 * (same key, or simply the same target) returns a body byte-identical to the
 * first call's, so "a retry does not double-insert AND returns the same result"
 * is a single `toStrictEqual` assertion rather than a judgement call. A 201 on
 * the first call and 200 on the retry would have broken exactly that property.
 *
 * `updatedAt` is the LWW token (AC 10.6): server-assigned by the
 * `favorite_set_updated_at` trigger, never taken from the request.
 */
export const favoriteResponseSchema = z.strictObject({
    id: uuidSchema,
    targetType: favoriteTargetTypeSchema,
    targetId: uuidSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// Playlists (TASK 6.2 — US-8 / US-9)
// ---------------------------------------------------------------------------
/** AC 9.1 name bound, measured in CODE POINTS (see {@link playlistNameSchema}). */
export const PLAYLIST_NAME_MAX_CHARS = 100;
/**
 * Cheap pre-bound on the RAW, un-normalized name — and the 8x is not a
 * superstition, it is headroom over the worst case of a DIFFERENT unit.
 *
 * `z.string().max(n)` measures `String.length`, i.e. UTF-16 CODE UNITS. The
 * real rule ({@link playlistNameSchema}) measures CODE POINTS, matching the
 * database's `char_length` CHECK. Those units differ by up to 2x (every
 * astral-plane character — emoji, most CJK extensions — is a surrogate pair,
 * so 100 code points can legitimately be 200 units), and NFC normalization
 * runs AFTER this bound and is not guaranteed to shrink its input.
 *
 * So the pre-bound MUST be comfortably above 2x or it would reject names the
 * real rule accepts — a bug that would only ever show up for emoji users. 8x
 * (800 units, at most 1.6 KB of UTF-8) is four times the worst legitimate case
 * and still small enough that normalizing and spreading it into an array is
 * free, which is the only job it has: stop a megabyte of text before
 * `.normalize()` and `[...s]` touch it.
 */
export const PLAYLIST_NAME_RAW_MAX_UTF16_UNITS = PLAYLIST_NAME_MAX_CHARS * 8;
/** AC 9.4 — tracks per playlist. */
export const PLAYLIST_TRACKS_MAX = 5000;
/** AC 9.4 — playlists per user. */
export const PLAYLISTS_PER_USER_MAX = 500;
/**
 * THERE IS DELIBERATELY NO PER-REQUEST ADD BATCH CAP. One existed (500 tracks)
 * and it quietly narrowed AC 9.2 ("add a whole album", stated with no size
 * exception): an album larger than 500 — which the sync job can produce from a
 * Drive folder that collapsed many files into one album — was refused WHOLE and
 * had no chunked-add path to fall back on, so it was permanently unaddable. The
 * bound that remains is {@link PLAYLIST_TRACKS_MAX}, which is the bound the
 * playlist itself has, so an album that fits can always be added in one call.
 */
/**
 * Canonical form of a user-entered playlist name: NFC-normalized, then
 * trimmed.
 *
 * THE NORMALIZATION IS NOT COSMETIC. Postgres compares `text` BYTEWISE, so
 * "Café" typed with a precomposed U+00E9 and "Café" typed with e + U+0301 are
 * two DIFFERENT rows under `UNIQUE (user_id, name)` while rendering
 * identically in every list. A user would see two apparently identical
 * playlists and be unable to tell them apart. Normalizing to NFC before the
 * uniqueness comparison — and before storage, so the stored form IS the
 * compared form — collapses them to one.
 *
 * `String.prototype.trim` removes the full Unicode whitespace set (including
 * U+00A0 NO-BREAK SPACE), so padding cannot smuggle a blank name past the
 * minimum length either.
 */
export function normalizePlaylistName(raw) {
    return raw.normalize('NFC').trim();
}
/**
 * Playlist name: required, normalized, 1..100 CODE POINTS after normalization.
 *
 * The length is counted in code points (`[...s].length`), not UTF-16 units,
 * because the database CHECK is `char_length(name) BETWEEN 1 AND 100` and
 * `char_length` counts code points. Using `.max(100)` on the raw string would
 * measure UTF-16 units instead and reject a legal 60-emoji name; counting code
 * points makes the schema and the constraint agree exactly, so a request this
 * schema accepts can never trip the CHECK.
 *
 * The pre-bound on the RAW string exists so a megabyte of text is rejected
 * before it is normalized and spread into an array — see
 * {@link PLAYLIST_NAME_RAW_MAX_UTF16_UNITS} for why it is 8x and not 1x.
 */
export const playlistNameSchema = z
    .string()
    .max(PLAYLIST_NAME_RAW_MAX_UTF16_UNITS)
    .transform(normalizePlaylistName)
    .refine((s) => [...s].length >= 1 && [...s].length <= PLAYLIST_NAME_MAX_CHARS, {
    message: `Enter a name between 1 and ${String(PLAYLIST_NAME_MAX_CHARS)} characters.`,
});
export const createPlaylistRequestSchema = z.strictObject({ name: playlistNameSchema });
/** `PATCH /api/playlists/:id` — rename only; membership has its own routes. */
export const renamePlaylistRequestSchema = z.strictObject({ name: playlistNameSchema });
/** `POST /api/playlists/from-queue` (AC 8.1). Empty queue -> 422 (AC 8.4). */
export const playlistFromQueueRequestSchema = z.strictObject({ name: playlistNameSchema });
/** One row of `GET /api/playlists`; `updatedAt` is the LWW token (AC 9.7). */
export const playlistSummarySchema = z.strictObject({
    id: uuidSchema,
    name: z.string().min(1),
    trackCount: z.number().int().min(0),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const playlistListResponseSchema = z.strictObject({
    items: z.array(playlistSummarySchema),
    ...cursorPageFields,
});
/**
 * One track row inside a playlist. `position` is the SERVER-maintained
 * contiguous 0-based index — every mutation renumbers so that the set of
 * positions is exactly `0..trackCount-1`, which is what makes "move to
 * position N" well defined and what the reorder integration test asserts after
 * every operation.
 *
 * `itemId` (the `playlist_track` row id) is what reorder/remove address, NOT
 * `track.id`: the same track may legitimately appear twice (AC 9.2 "add
 * anyway"), so a track id does not identify a row.
 */
export const playlistTrackEntrySchema = z.strictObject({
    itemId: uuidSchema,
    position: z.number().int().min(0),
    addedAt: z.string(),
    track: trackCardSchema,
});
/**
 * `GET /api/playlists/:id` — header + ONE cursor page of tracks (<= 200), so a
 * 5,000-track playlist never serializes in a single response (W18 / §8).
 * `trackCount` stays the authoritative total across all pages.
 */
export const playlistDetailResponseSchema = z.strictObject({
    ...playlistSummarySchema.shape,
    items: z.array(playlistTrackEntrySchema),
    ...cursorPageFields,
});
/**
 * `POST /api/playlists/:id/tracks` — append one track, or a WHOLE ALBUM in
 * album order (disc, then track number), in ONE call.
 *
 * Discriminated on `source` rather than "one of trackId/albumId must be set"
 * so the two cases cannot be sent together and the server never has to guess.
 * An album add is atomic: if it would cross {@link PLAYLIST_TRACKS_MAX} the
 * whole add is refused and NOTHING is inserted (AC 9.4 no-partial-apply). That
 * cap is the ONLY size limit — an album of any length that fits in the playlist
 * goes in with one call; see the note on {@link PLAYLISTS_PER_USER_MAX}'s
 * neighbour above for why a smaller per-request cap was removed.
 */
export const addPlaylistTracksRequestSchema = z.discriminatedUnion('source', [
    z.strictObject({
        source: z.literal('track'),
        trackId: uuidSchema,
        idempotencyKey: idempotencyKeySchema.optional(),
    }),
    z.strictObject({
        source: z.literal('album'),
        albumId: uuidSchema,
        idempotencyKey: idempotencyKeySchema.optional(),
    }),
]);
/** `POST /api/playlists/:id/reorder` — see {@link moveDirectiveSchema}. */
export const reorderPlaylistRequestSchema = z.strictObject({
    itemId: uuidSchema,
    move: moveDirectiveSchema,
});
// ---------------------------------------------------------------------------
// Queue (TASK 6.3 — US-7)
// ---------------------------------------------------------------------------
/** AC 7.1 — hard cap on queued items. */
export const QUEUE_ITEMS_MAX = 1000;
/** Tracks one `add`/`playNext` op may append (an album, padded). */
export const QUEUE_ADD_BATCH_MAX = 200;
/**
 * Rows one `GET /api/queue/reconcile` CHUNK may carry. Reconciliation must
 * exchange the WHOLE ordered queue, but "whole" and "unbounded" are not the
 * same thing: at 500/chunk a maximal 1,000-item queue is exactly two bounded
 * round-trips. Documented here because it is part of the sync contract, not an
 * implementation detail — a client loops on `nextAfter` until it is null.
 */
export const QUEUE_RECONCILE_CHUNK_MAX = 500;
/**
 * The queue VERSION token — a monotonic, opaque string derived from the
 * server-assigned `queue_state.updated_at`:
 *
 *     to_char(updated_at AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.US"Z"')
 *
 * FIXED WIDTH AND UTC ON PURPOSE, so byte-wise string comparison IS chronological
 * comparison — a client (or a test) can order two tokens without parsing them,
 * and a server whose session TimeZone differs cannot render a token that sorts
 * differently. Microsecond resolution because two mutations by the same user can
 * land in the same millisecond.
 *
 * MONOTONIC IS A GUARANTEE ABOUT THIS ROW, and it is the trigger that provides
 * it: `set_updated_at` writes
 * `greatest(clock_timestamp(), OLD.updated_at + 1 microsecond)` (migration
 * 0006), so successive tokens for ONE queue strictly increase, and because the
 * trigger runs under the API's per-user lock they increase in commit order.
 * It was NOT true before 0006, when the column held `now()` — transaction-start
 * time — and a transaction that began first but committed last published an
 * older token than one already delivered to a client.
 *
 * {@link QUEUE_VERSION_NONE} is the token for a user who has never had a queue.
 * `'0'` sorts before every real token (0x30 < 0x32), so "no queue yet" is
 * strictly older than any state, with no special-casing at the comparison site.
 */
/**
 * Length bound on the token as it arrives from a CLIENT. A real token is
 * exactly 27 characters (`YYYY-MM-DDTHH:MM:SS.ffffffZ`) and
 * {@link QUEUE_VERSION_NONE} is one; the bound is not a format check (the
 * server compares for equality against a token it minted, so a malformed value
 * simply fails to match and yields a 409) but a cheap ceiling that keeps a
 * megabyte of text out of the comparison and out of the logs. Named rather
 * than inline so it reads like its neighbours
 * ({@link IDEMPOTENCY_KEY_MAX_CHARS}, {@link CLIENT_EVENT_ID_MAX_CHARS}).
 */
export const QUEUE_VERSION_MAX_CHARS = 64;
export const queueVersionSchema = z.string().min(1).max(QUEUE_VERSION_MAX_CHARS);
export const QUEUE_VERSION_NONE = '0';
/** One queued item. `itemId` addresses the row; the same track may repeat. */
export const queueItemSchema = z.strictObject({
    itemId: uuidSchema,
    position: z.number().int().min(0),
    track: trackCardSchema,
});
/**
 * `GET /api/queue` — the DISPLAY read: one cursor page of <= 200 items.
 *
 * THIS IS NOT THE RECONCILIATION READ (AC 7.5 / W18). Paging is for rendering
 * the up-next list; a client that tried to rebuild its local queue by walking
 * these pages could interleave a concurrent mutation between two pages and
 * assemble an order that never existed. Reconciliation uses
 * `GET /api/queue/reconcile`, whose `version` makes exactly that interleaving
 * detectable.
 */
export const queueResponseSchema = z.strictObject({
    version: queueVersionSchema,
    currentIndex: z.number().int().min(0),
    totalItems: z.number().int().min(0),
    items: z.array(queueItemSchema),
    ...cursorPageFields,
});
/**
 * `GET /api/queue/reconcile` query — `after` is a POSITION, not an opaque
 * cursor, because the reconciliation order is the positional order itself and
 * saying so plainly makes the contract auditable.
 *
 * `after` IS BOUNDED BY THE QUEUE CAP, and the bound is a 500-prevention, not
 * a nicety. The value is bound to `$2::int` in services/queue.ts; without a
 * maximum, `?after=2147483648` reached Postgres as an int4 cast of a value
 * int4 cannot hold and came back as a 22003 — a 500 INTERNAL, emitted at
 * 100 req/min by any allowlisted member, polluting the S8 error-rate alert.
 * (Parameterization was never in doubt; the value was bound, not
 * interpolated.) The tightest CORRECT bound is the queue cap: positions are
 * contiguous from 0 and capped at {@link QUEUE_ITEMS_MAX} items, so the
 * highest position that can exist is `QUEUE_ITEMS_MAX - 1` and a larger
 * `after` names a row the schema forbids. Rejecting it 422 is what
 * services/pagination.ts's cursor contract already promises for every other
 * client-supplied key: "never a 500".
 */
export const queueReconcileQuerySchema = z.object({
    after: z.coerce
        .number()
        .int()
        .min(0)
        .max(QUEUE_ITEMS_MAX - 1)
        .optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .transform((n) => Math.min(n, QUEUE_RECONCILE_CHUNK_MAX))
        .default(QUEUE_RECONCILE_CHUNK_MAX),
});
/**
 * `GET /api/queue/reconcile` — one bounded chunk of the FULL ordered queue.
 *
 * THE SYNC CONTRACT, precisely (AC 7.5):
 *   1. On app start the client walks `after`/`nextAfter` until `nextAfter` is
 *      null, then adopts the assembled queue verbatim — SERVER WINS.
 *   2. `version` is captured on every chunk. If it CHANGES between chunks the
 *      client discards the partial assembly and restarts the walk; the token's
 *      monotonicity is what makes "changed" cheap to detect.
 *   3. During an active session the client pushes each mutation through
 *      `PUT /api/queue` carrying its last-known `version` — LOCAL WINS while
 *      the version still matches. A stale token means another device wrote
 *      first: the server answers 409 CONFLICT and the client re-runs step 1
 *      rather than clobbering that write.
 */
export const queueReconcileResponseSchema = z.strictObject({
    version: queueVersionSchema,
    currentIndex: z.number().int().min(0),
    totalItems: z.number().int().min(0),
    items: z.array(queueItemSchema),
    /** Position to send as the next `after`, or null on the last chunk. */
    nextAfter: z.number().int().min(0).nullable(),
});
/**
 * `PUT /api/queue` ops.
 *
 * `clear` KEEPS THE CURRENTLY-PLAYING ITEM (AC 7.1): "clear the queue" means
 * "drop what is up next", not "stop the music". `setCurrentIndex` is how a
 * client persists playback advancing; it is separate from the history write
 * (`POST /api/queue/history`) because advancing and "this track finished" are
 * different facts — a user scrubbing through the queue advances without
 * finishing anything, and AC 7.3 forbids recording that.
 */
export const queueOpSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('add'),
        trackIds: z.array(uuidSchema).min(1).max(QUEUE_ADD_BATCH_MAX),
        idempotencyKey: idempotencyKeySchema.optional(),
    }),
    z.strictObject({
        kind: z.literal('playNext'),
        trackIds: z.array(uuidSchema).min(1).max(QUEUE_ADD_BATCH_MAX),
        idempotencyKey: idempotencyKeySchema.optional(),
    }),
    z.strictObject({ kind: z.literal('reorder'), itemId: uuidSchema, move: moveDirectiveSchema }),
    z.strictObject({ kind: z.literal('remove'), itemId: uuidSchema }),
    z.strictObject({ kind: z.literal('clear') }),
    z.strictObject({ kind: z.literal('setCurrentIndex'), index: z.number().int().min(0) }),
]);
/**
 * `PUT /api/queue` body. `version` is OPTIONAL and its absence is meaningful:
 * a client that has just reconciled (or is doing a first write) omits it and
 * the mutation applies unconditionally. Once a client holds a version it should
 * always send it — that is what converts a silent clobber into a 409.
 */
export const queueMutationRequestSchema = z.strictObject({
    version: queueVersionSchema.optional(),
    op: queueOpSchema,
});
/**
 * `PUT /api/queue` result — the queue's new STATE, deliberately without the
 * items (which are bounded reads of their own) and deliberately without a
 * "how many were added" field.
 *
 * Same reasoning as {@link favoriteResponseSchema}: because the body reports
 * state rather than effect, an idempotent retry — which inserts nothing and so
 * does not bump `updated_at` — returns a byte-identical body including the
 * SAME `version`. That single equality is the whole "a retry does not
 * double-insert" assertion.
 */
export const queueMutationResponseSchema = z.strictObject({
    version: queueVersionSchema,
    currentIndex: z.number().int().min(0),
    totalItems: z.number().int().min(0),
});
// ---------------------------------------------------------------------------
// Queue history (TASK 6.4 — US-7, AC 7.3)
// ---------------------------------------------------------------------------
/** AC 7.3 — rows retained per user; older ones are pruned as new ones land. */
export const QUEUE_HISTORY_RETAIN = 100;
/**
 * WHY A HISTORY ROW EXISTS AT ALL. AC 7.3 admits exactly two facts — a track
 * FINISHED, or it was SKIPPED — and forbids recording anything else, in
 * particular a track that sat in the queue and was cleared before it ever
 * played. The enum IS that rule: there is no third value a client could send,
 * and no other route writes this table, so "queued-then-cleared" is
 * unrepresentable rather than merely unwritten.
 */
export const queueHistoryReasonSchema = z.enum(['finished', 'skipped']);
/**
 * `POST /api/queue/history` — the ONE write path for queue history.
 *
 * DEVICE-AGNOSTIC (AC 7.3): the body carries no device/session identifier and
 * the row carries no device column, so a track finished on a phone is the same
 * history entry the desktop reads back. `occurred_at` is server-assigned — a
 * client clock never decides which entries survive the last-100 prune.
 *
 * `idempotencyKey` carries {@link idempotencyKeySchema}'s meaning unchanged.
 * It is here because this IS an add-type mutation and it is the one a flaky
 * network makes a client repeat most: the request fires the moment a track
 * ends, and a retry after a timeout used to write a second row AND evict a real
 * entry from the retained hundred. A replay returns the ORIGINAL entry.
 */
export const recordQueueHistoryRequestSchema = z.strictObject({
    trackId: uuidSchema,
    reason: queueHistoryReasonSchema,
    idempotencyKey: idempotencyKeySchema.optional(),
});
export const queueHistoryEntrySchema = z.strictObject({
    id: uuidSchema,
    occurredAt: z.string(),
    reason: queueHistoryReasonSchema,
    track: trackCardSchema,
});
export const queueHistoryResponseSchema = z.strictObject({
    items: z.array(queueHistoryEntrySchema),
    ...cursorPageFields,
});
/**
 * `POST /api/queue/history/:id/readd` — append a history entry's track back to
 * the queue, honouring the {@link QUEUE_ITEMS_MAX} cap exactly like any other
 * add. The body is optional; only an idempotency key can appear in it, because
 * WHICH track is re-added comes from the path id (which is ownership-scoped),
 * never from the caller.
 */
export const readdQueueHistoryRequestSchema = z.strictObject({
    idempotencyKey: idempotencyKeySchema.optional(),
});
// ---------------------------------------------------------------------------
// PlayEvent ingest (TASK 6.5 — US-12, AC 12.1 / 12.4 / 13.7)
// ---------------------------------------------------------------------------
/**
 * Hard batch cap. `errors.ts` documents PAYLOAD_TOO_LARGE as "telemetry batch
 * > 50 events", so a 51-event batch is a 413 — NOT a 422. The distinction
 * matters to an offline client: a 422 says "this batch is malformed, fix it",
 * a 413 says "this batch is too big, split it and retry", and only the second
 * is actionable for a device that has accumulated a week of events.
 */
export const PLAY_EVENT_BATCH_MAX = 50;
export const CLIENT_EVENT_ID_MAX_CHARS = 128;
/**
 * Charset for `clientEventId`, deliberately the SAME expression
 * `plugins/logging.ts` applies to an incoming `x-request-id`
 * (`REQUEST_ID_PATTERN`).
 *
 * The reason is PII hygiene, not injection: JSON encoding already neutralizes
 * log forging, but this id is copied verbatim into a Cloud Logging entry that
 * is retained for 90 days, and an unconstrained 128-char field is somewhere a
 * client can park an email address or a bearer token for a quarter of a year.
 * The module's own guarantee is that log BODIES stay PII-clean, and a charset
 * that admits no `@` and no whitespace is what makes that structural rather
 * than a rule clients are trusted to follow. Every id the PWA mints is a
 * uuid/ULID, which this admits unchanged.
 */
export const CLIENT_EVENT_ID_PATTERN = /^[A-Za-z0-9._-]+$/;
/**
 * Upper bound on the `errorCode` slug — 64 chars, named rather than inline so
 * the DTO bound and the ops doc quote one number.
 */
export const PLAY_EVENT_ERROR_CODE_MAX_CHARS = 64;
/**
 * `errorCode` is a machine SLUG, not free text, and the charset is the point.
 *
 * It lands verbatim in the 90-day Cloud Logging entry as `error_code` and is
 * the field the playback-reliability breakdown groups by, so it must be
 * low-cardinality and PII-free by construction. Lower-case ASCII words joined
 * by `_` (`decode_error`, `network_timeout`, `http_410`) satisfies both: there
 * is no `@`, no space and no punctuation an email or a token could survive.
 *
 * NOT an enum: the client owns the failure taxonomy (media-element error
 * codes, HTTP statuses, codec probes) and a closed server-side list would
 * force a coordinated release for every new failure mode — which is exactly
 * when telemetry matters most. The charset is the guarantee; the vocabulary is
 * documented in docs/ops/telemetry-log-contract.md.
 */
export const PLAY_EVENT_ERROR_CODE_PATTERN = /^[a-z][a-z0-9_]*$/;
/**
 * The failure slug as validated on the wire — shared by `failureReason` (the
 * canonical request field) and `errorCode` (its legacy alias), so the two
 * spellings can never drift in what they admit.
 */
const playEventFailureSlugSchema = z
    .string()
    .trim()
    .min(1)
    .max(PLAY_EVENT_ERROR_CODE_MAX_CHARS)
    .regex(PLAY_EVENT_ERROR_CODE_PATTERN, 'Use a lower-case slug such as decode_error.');
/** Upper bound on one event's cumulative play time (24 h; int4-safe). */
export const PLAY_EVENT_MS_PLAYED_MAX = 86_400_000;
/** Tolerated clock skew for a client-stamped `playedAt` in the future. */
export const PLAY_EVENT_FUTURE_SKEW_MS = 5 * 60 * 1000;
/** How far back an offline batch may reach (matches the offline retention). */
export const PLAY_EVENT_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
export const playEventSourceSchema = z.enum(['stream', 'offline']);
export const playEventContextTypeSchema = z.enum([
    'album',
    'playlist',
    'favorites',
    'queue',
    'search',
]);
/**
 * WHO STARTED THIS PLAY — PRD §11 `track_play_started.initiation ∈ {user, auto}`.
 *
 * IT IS LOAD-BEARING, not a nice-to-have label. The PRD defines the playback
 * reliability SLI as "`track_play_started` (**all** starts — both
 * `initiation ∈ {user, auto}`) vs `track_play_failed` ratio over a 7-day
 * window". A metric filter cannot express "all starts across initiation" over
 * entries that do not carry the field at all, so without it the TASK 11.1
 * denominator is not definable and the alert has nothing to fire on.
 *
 *   user   a person pressed play / picked a track / hit next.
 *   auto   the engine started it: auto-advance on `ended`, shuffle, repeat
 *          (AC 5.4 / 6.5) — the starts a user never asked for and therefore
 *          the ones a reliability regression hides in.
 *
 * DEFAULTS TO `user` when a client omits it. That default cannot skew the SLI
 * it exists for, because the denominator is ALL starts regardless of
 * initiation; it only labels an unlabelled start as the commoner of the two,
 * which is the honest guess for a client old enough not to send the field.
 */
export const playEventInitiationSchema = z.enum(['user', 'auto']);
/**
 * Context types that CARRY AN IDENTITY, and therefore require a `contextId`
 * that resolves. `favorites` / `queue` / `search` are ephemeral surfaces with
 * nothing to point at — there is no "the favorites you played on Tuesday" page
 * — so they must arrive WITHOUT an id, matching how the seed stores them and
 * how services/stats-rollup.ts already reads them (a null-id context is
 * excluded from the recently-played rail by rule 2 there).
 *
 * This is a deliberate sharpening of "context_id is present iff context_type is
 * set": taken literally that would force an id onto the three types that have
 * none. The rule actually enforced is stricter in both directions —
 * album/playlist REQUIRE an id, the other three FORBID one, and no type at all
 * forbids one too.
 */
export const IDENTIFIED_PLAY_CONTEXT_TYPES = ['album', 'playlist'];
/**
 * One play event in a batch.
 *
 * `msPlayed` is CUMULATIVE for this event (AC 12.1's word). That is what makes
 * repeat-one count correctly: each replay is a SEPARATE event with its own
 * `clientEventId` and its own cumulative total, so each independent crossing of
 * the 30 s / 50% threshold counts once, while a paused-and-resumed single
 * listen accumulates into one event and counts once.
 *
 * THREE FIELDS EXIST FOR THE LOG SINK ONLY and never reach Postgres —
 * `initiation`, `coldStart` and `failureReason` (legacy alias: `errorCode`).
 * `play_event` has no column for any
 * of them and inventing one would drag diagnostics into the stats source of
 * truth (and into the INDEFINITE retention `PlayEvent` alone gets). They drive
 * the `track_play_*` Cloud Logging entries that back the playback-reliability
 * and start-latency metrics (W5 / TASK 11.1); the published shape of those
 * entries is {@link playbackTelemetryEntrySchema} and
 * docs/ops/telemetry-log-contract.md.
 *
 * `coldStart` IS CLIENT-SUPPLIED, and it has to be. Per the plan's TASK 7.4
 * step 5 (W15) the client reads the `X-Cold-Start` response header off the
 * STREAM request and reports it here. The server cannot substitute its own
 * cold-start state: `POST /api/play-events` is a different request, usually to
 * a different (already warm) instance, minutes or days after the audio started
 * — its instance age says nothing about whether the play it describes was
 * served cold. Absent means "the client did not know", which is emitted as
 * `cold_start: null` rather than guessed as `false`, so the AC 8.x latency SLI
 * (p95 over `cold_start = false`) never silently absorbs unknowns.
 */
export const playEventInputSchema = z
    .strictObject({
    clientEventId: z
        .string()
        .trim()
        .min(1)
        .max(CLIENT_EVENT_ID_MAX_CHARS)
        .regex(CLIENT_EVENT_ID_PATTERN, 'Use only letters, digits, dot, dash or underscore.'),
    trackId: uuidSchema,
    playedAt: z.iso.datetime({ offset: true }),
    msPlayed: z.number().int().min(0).max(PLAY_EVENT_MS_PLAYED_MAX),
    source: playEventSourceSchema,
    initiation: playEventInitiationSchema.default('user'),
    coldStart: z.boolean().optional(),
    contextType: playEventContextTypeSchema.optional(),
    contextId: uuidSchema.optional(),
    /**
     * The client's report that this playback attempt FAILED — the field that
     * makes the event emit `track_play_failed` (as `error_code`) alongside
     * `track_play_started`. Same slug rules as {@link PLAY_EVENT_ERROR_CODE_PATTERN}.
     */
    failureReason: playEventFailureSlugSchema.optional(),
    /** LEGACY ALIAS for `failureReason` — older clients send this spelling. */
    errorCode: playEventFailureSlugSchema.optional(),
})
    .superRefine((value, ctx) => {
    const identified = value.contextType !== undefined &&
        IDENTIFIED_PLAY_CONTEXT_TYPES.includes(value.contextType);
    if (identified && value.contextId === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['contextId'],
            message: 'An album or playlist context must carry the id it played from.',
        });
    }
    if (!identified && value.contextId !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['contextId'],
            message: 'This context type has no id to reference.',
        });
    }
});
export const playEventBatchRequestSchema = z.strictObject({
    events: z.array(playEventInputSchema).min(1).max(PLAY_EVENT_BATCH_MAX),
});
/**
 * `POST /api/play-events` result.
 *
 * UNLIKE the state resources above, this endpoint DOES report what the call
 * did, and the difference is intentional: an event sink's caller needs to know
 * which of its buffered events it may now delete. `stored + duplicates ==
 * received` always, and a full replay of an already-merged batch returns
 * `stored: 0, duplicates: n` — which is precisely the observable proof that
 * the merge did not double-count (AC 12.4). The DURABLE state is what is
 * idempotent here; the receipt describes the absorption.
 *
 * `counted` is how many of the STORED events met the AC 12.1 threshold and so
 * moved the `play_stat_daily` rollup.
 */
export const playEventIngestResponseSchema = z.strictObject({
    received: z.number().int().min(0),
    stored: z.number().int().min(0),
    duplicates: z.number().int().min(0),
    counted: z.number().int().min(0),
});
/**
 * The playback-reliability Cloud Logging entries (W5).
 *
 * =========================================================================
 * THIS IS A PUBLISHED CONTRACT, NOT AN INTERNAL SHAPE
 * =========================================================================
 * `infra/terraform/metrics.tf` (TASK 11.1) will be authored against these
 * exact strings, and a Cloud Logging metric filter that names a field this
 * code does not emit matches ZERO entries and fails SILENTLY — no error, no
 * empty-metric warning, just an alert that never fires. So the field names
 * here are the plan's TASK 11.1 step-1 names, verbatim, and
 * docs/ops/telemetry-log-contract.md pins them outside TypeScript for whoever
 * writes that Terraform.
 *
 * SNAKE_CASE, ON PURPOSE. The plan names `event`, `request_id`, `cold_start`,
 * `initiation`, `context_type`, `error_code`, `track_id`, `query_length`, … —
 * every published jsonPayload field across every §11 event is snake_case. The
 * rest of this file is camelCase because it describes JSON the PWA consumes;
 * this object describes a log entry a metric filter consumes, and mixing the
 * two conventions inside one payload is precisely how a filter ends up
 * matching nothing. The mapping from the camelCase request field to the
 * snake_case log field happens once, in services/playevents.ts.
 *
 * NESTED UNDER {@link PLAYBACK_TELEMETRY_LOG_KEY} rather than flattened onto
 * jsonPayload — a DELIBERATE divergence from the plan's wording, which reads
 * as though the fields sit at the top level. Flattening would put `event`,
 * `source` and `request_id` next to pino's own top-level keys (`level`,
 * `severity`, `time`, `requestId`, `msg`, `err`) where a future logging-plugin
 * field could collide with a metric filter's path and change what it matches.
 * The cost is one extra path segment in every filter
 * (`jsonPayload.playbackTelemetry.event`), which the ops doc states.
 *
 * SEVERITY IS PART OF THE CONTRACT TOO: `track_play_failed` is emitted at WARN
 * (severity WARNING) and the other two at INFO, so a playback failure reaches
 * severity>=WARNING surfaces without needing the metric at all. See
 * services/telemetry.ts.
 *
 * TWO SINKS, NEVER RECONCILED (note-fold R3). The Postgres `play_event` row is
 * AUTHORITATIVE for stats and is retained indefinitely; this entry is a
 * DIAGNOSTIC DUPLICATE retained 90 days that feeds the reliability metric only.
 * They will disagree — a log entry can be dropped by the agent, a row can be
 * written while the emit fails — and nothing tries to make them agree. No user
 * id is emitted: `request_id` already correlates the line, and the logging
 * plugin's PII-clean rule keeps identities out of log bodies.
 */
export const PLAYBACK_TELEMETRY_EVENTS = [
    'track_play_started',
    'track_play_counted',
    'track_play_failed',
];
/**
 * The per-event half of the entry — everything services/playevents.ts can
 * derive from the batch alone. {@link playbackTelemetryEntrySchema} adds the
 * request-scoped half.
 */
export const playbackTelemetryEventSchema = z.strictObject({
    event: z.enum(PLAYBACK_TELEMETRY_EVENTS),
    track_id: uuidSchema,
    client_event_id: z.string().min(1).max(CLIENT_EVENT_ID_MAX_CHARS),
    source: playEventSourceSchema,
    ms_played: z.number().int().min(0),
    /** PRD §11: the SLI denominator is ALL starts across `{user, auto}`. */
    initiation: playEventInitiationSchema,
    /** Null when the client did not report one — never guessed as `false`. */
    cold_start: z.boolean().nullable(),
    /** Null for a standalone play — the key is always present, for stable filters. */
    context_type: playEventContextTypeSchema.nullable(),
    /**
     * WHEN THE MUSIC PLAYED (the client's `playedAt`), NOT when the entry was
     * written.
     *
     * A log entry's own timestamp is INGEST time, and AC 13.7 explicitly
     * contemplates an offline device flushing days of events at once. Without
     * this field the 7-day reliability window buckets a week of that device's
     * starts and failures into the single minute it reconnected — an artifact
     * that can trip the "7-day < 97%" alert on its own and that no downstream
     * query can undo, because the information was never emitted. With it, the
     * metric can bucket on `played_at`.
     */
    played_at: z.iso.datetime({ offset: true }),
    /** Present on `track_play_failed` only; a slug, never free text. */
    error_code: z.string().min(1).max(PLAY_EVENT_ERROR_CODE_MAX_CHARS).optional(),
});
/**
 * What is actually WRITTEN to the log — the event plus the correlation id.
 *
 * `request_id` is added by the sink because only the request layer knows it.
 * It duplicates pino's top-level `requestId` binding on purpose: the plan names
 * `request_id` as a jsonPayload field of every §11 entry, and a metric LABEL
 * extractor has to read one path, so the value lives inside the entry rather
 * than the metric author having to know that this one field is spelled
 * differently and lives one level up.
 */
export const playbackTelemetryEntrySchema = playbackTelemetryEventSchema.extend({
    request_id: z.string().min(1),
});
/** The `jsonPayload` key the playback metric filters anchor on. */
export const PLAYBACK_TELEMETRY_LOG_KEY = 'playbackTelemetry';
/**
 * The `telemetry_write_failure` structured entry (TASK 11.1 §1 / §2).
 *
 * WHY IT IS AN EVENT AND NOT JUST A COUNTER. The durable
 * `service_counter.telemetry_write_failure` row is ALL-TIME by design
 * (N-OBS-1: it backs the AC 3.5a never-null at-rest readout). TASK 11.1's
 * alert is a WINDOW condition — "> 1% or persisting 3 h" — and an all-time
 * counter cannot express either half of that. Without a structured entry the
 * only windowed signal available to `metrics.tf` would be a filter on a free
 * text `msg` string, which is exactly the kind of accidental contract that
 * breaks the next time someone reworded a log line.
 *
 * A SEPARATE ROOT KEY from {@link PLAYBACK_TELEMETRY_LOG_KEY}: this describes
 * the health of the SINK, not a playback attempt, and its fields are disjoint.
 * Filters key on `jsonPayload.telemetryHealth.event`, and both roots are
 * listed in docs/ops/telemetry-log-contract.md.
 *
 * Emitted at WARN (severity WARNING) — never INFO — because every kind below
 * means diagnostics were lost.
 */
export const TELEMETRY_HEALTH_LOG_KEY = 'telemetryHealth';
/**
 * How the write failed. Three distinguishable causes, because the operator
 * response differs: the sink itself refusing writes, the durable counter being
 * unreachable (so even the all-time number is now wrong), and shutdown
 * outrunning the drain (so the entries were never attempted again at all).
 */
export const TELEMETRY_FAILURE_KINDS = [
    'sink_exhausted',
    'counter_write_failed',
    'drain_deadline_exceeded',
];
export const telemetryHealthEntrySchema = z.strictObject({
    event: z.literal('telemetry_write_failure'),
    kind: z.enum(TELEMETRY_FAILURE_KINDS),
    /** Sink attempts made before giving up; 0 when no attempt was possible. */
    attempts: z.number().int().min(0),
    /** How many playback entries were lost — the number the alert is about. */
    dropped_entries: z.number().int().min(0),
    /** Absent on shutdown paths, which belong to no request. */
    request_id: z.string().min(1).optional(),
});
//# sourceMappingURL=dto.js.map